import { injectable } from 'tsyringe';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';
import type {
    CategoryAssistPlan,
    CategoryAssistRequest,
    SubcategoryGenerationPlan,
    SubcategoryGenerationRequest,
} from '@/shared/assist/CategoryAssistContract';
import type { ICategorySuggestionEngine } from './CategorySuggestionEngine';

const CATEGORY_ASSIST_ROUTE = '/_assist/categories';
const CATEGORY_SUBCATEGORY_ROUTE = '/_assist/categories/subcategories';

/** Calls the BFF usage-category assist routes over the authenticated fetch (CSRF header + session cookie). */
@injectable()
export class BffCategorySuggestionEngine implements ICategorySuggestionEngine {
    async suggest(request: CategoryAssistRequest): Promise<CategoryAssistPlan> {
        const response = await authenticatedFetch(CATEGORY_ASSIST_ROUTE, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(request),
        });
        if (!response.ok) {
            throw new Error(await readError(response));
        }
        return (await response.json()) as CategoryAssistPlan;
    }

    async generateSubcategories(request: SubcategoryGenerationRequest): Promise<SubcategoryGenerationPlan> {
        const response = await authenticatedFetch(CATEGORY_SUBCATEGORY_ROUTE, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(request),
        });
        if (!response.ok) {
            throw new Error(await readError(response));
        }
        return (await response.json()) as SubcategoryGenerationPlan;
    }
}

async function readError(response: Response): Promise<string> {
    const body = await response
        .clone()
        .json()
        .then((value) => value as { error?: string })
        .catch(() => null);
    if (body !== null && typeof body.error === 'string' && body.error.length > 0) {
        return body.error;
    }
    return `Category assist request failed (${response.status})`;
}
